<script setup>
import { reactive, computed, onMounted, ref } from "vue";
import axios from "axios";
import { useRouter } from "vue-router";
import router from "@/router";
import store from "@/store";

const { currentRoute } = useRouter();

const state = reactive({
  conference: {
    contents: "",
    conferenceDetailList: [],
  },
});

/*axios.get('/api/conferences').then(response => {
  state.conferences = response.data.contents
}).catch(error => {
  console.error(error)
})*/

// 접수 상태 - before: 접수 전, open: 접수 중, closed: 접수 마감
const getPeriodState = (rsdatetime, redatetime) => {
  const today = new Date();
  if (today < new Date(Date.parse(rsdatetime))) return "before";
  if (today > new Date(Date.parse(redatetime))) return "closed";
  return "open";
};

const PERIOD_LABELS = {
  before: "접수 예정",
  open: "접수 중",
  closed: "접수 마감",
};

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

// 10월 31일 (토)
const formatShortDay = (d) =>
  `${d.getMonth() + 1}월 ${d.getDate()}일 (${WEEKDAYS[d.getDay()]})`;

// 2026년 10월 31일 (토)
const formatDay = (d) => `${d.getFullYear()}년 ${formatShortDay(d)}`;

// 오전 10시, 오후 7시 30분
const formatTime = (d) => {
  const hours = d.getHours();
  const minutes = d.getMinutes();
  return `${hours < 12 ? "오전" : "오후"} ${hours % 12 || 12}시${
    minutes ? ` ${minutes}분` : ""
  }`;
};

// 행사 일시
const formatSchedule = (date) => {
  const d = new Date(date);
  return `${formatDay(d)} ${formatTime(d)}`;
};

// 등록 마감 (연도는 행사 일시에 표기) - 23:59는 '밤 12시', 00:00은 '0시'로 표기
const formatDeadline = (date) => {
  const d = new Date(date);
  const hours = d.getHours();
  const minutes = d.getMinutes();

  if (hours === 23 && minutes === 59) {
    return `${formatShortDay(d)} 밤 12시`;
  }
  if (hours === 0 && minutes === 0) {
    return `${formatShortDay(d)} 0시`;
  }
  return `${formatShortDay(d)} ${formatTime(d)}`;
};

// 차수별 일정 - 같은 차수의 부서별 접수를 하나로 묶는다
const sessions = computed(() => {
  const groups = [];

  state.conference.conferenceDetailList.forEach((detail) => {
    if (detail.view === false) return;

    let group = groups.find((g) => g.number === detail.number);
    if (!group) {
      // 입력값에 섞인 공백·BOM 제거 (지역 중복 표기 방지)
      const local = (detail.local ?? "").trim();
      const place = (detail.place ?? "").trim();
      group = {
        number: detail.number,
        label: detail.number === 6 ? "추가" : `${detail.number}차`,
        place: [local, place].filter(Boolean).join(" · "),
        local,
        schedule: detail.schedule,
        redatetime: detail.redatetime,
        period: getPeriodState(detail.rsdatetime, detail.redatetime),
        depts: [],
      };
      groups.push(group);
    }

    // 노출하지 않는 접수 항목
    if (detail.idx !== 57 && detail.idx !== 58) {
      group.depts.push(detail);
    }
  });

  return groups;
});

// 상단 요약 - 전체 접수 상태
const overallPeriod = computed(() => {
  const periods = sessions.value.map((session) => session.period);
  if (periods.includes("open")) return "open";
  if (periods.includes("before")) return "before";
  return "closed";
});

// 상단 요약 - 일정 범위
const scheduleRange = computed(() => {
  const dates = sessions.value
    .map((session) => new Date(session.schedule))
    .sort((a, b) => a - b);
  if (dates.length === 0) return "";

  const first = dates[0];
  const last = dates[dates.length - 1];
  if (first.toDateString() === last.toDateString()) return formatDay(first);

  return `${formatDay(first)} ~ ${
    first.getFullYear() === last.getFullYear()
      ? formatShortDay(last)
      : formatDay(last)
  }`;
});

// 상단 요약 - 지역
const locals = computed(() => [
  ...new Set(sessions.value.map((session) => session.local).filter(Boolean)),
]);

// 잔여석 노출 여부
const showSeats = computed(
  () => state.conference.idx != 11 && conferenceIdx.value != 20,
);

// 차수 펼침 상태 - 지정하지 않으면 접수 마감된 차수만 접어 둔다
const expanded = reactive({});

const isExpanded = (session) =>
  expanded[session.number] ?? session.period !== "closed";

const toggleSession = (session) => {
  expanded[session.number] = !isExpanded(session);
};

const alertLogin = () => {
  alert("로그인 후 이용해주세요.");
  return;
};

const alertAvailable = () => {
  alert("접수 기간이 아닙니다.");
  return;
};

const alertExceed = () => {
  alert("접수가 마감되었습니다.");
  return;
};

const goApply = (idx) => {
  if (thisPageName === "conferenceListApp") {
    // document.location.href = `/app/register/${idx}`;
    router.push({ path: `/app/register/${idx}` });
  } else {
    // document.location.href = `/register/${idx}`;
    router.push({ path: `/register/${idx}` });
  }
};

const isFull = (dept) =>
  dept.limit_personnel != 0 &&
  dept.registered_personnel >= dept.limit_personnel;

// 부서별 신청 버튼 상태
const getDeptAction = (dept) => {
  const period = getPeriodState(dept.rsdatetime, dept.redatetime);
  if (period !== "open") {
    return {
      label: PERIOD_LABELS[period],
      disabled: true,
      onClick: alertAvailable,
    };
  }
  // 로그인 유무
  if (!store.state.account.id) {
    return isFull(dept)
      ? { label: "정원 마감", disabled: true, onClick: alertLogin }
      : { label: "신청", disabled: false, onClick: alertLogin };
  }
  // 인원 마감 시
  if (isFull(dept)) {
    return { label: "정원 마감", disabled: true, onClick: alertExceed };
  }
  return { label: "신청", disabled: false, onClick: () => goApply(dept.idx) };
};

// 모바일 - 접수 영역으로 이동
const registrationRef = ref(null);
const scrollToRegistration = () => {
  registrationRef.value?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const getConferenceList = () => {
  let idx = currentRoute._value.params.idx ?? "9";
  if (location.pathname === "/hana") {
    idx = "2";
  }
  axios
    .get(`/api/conference/${idx}`)
    .then((response) => {
      state.conference = response.data.contents;
      if (idx === "1") {
        state.conference.conferenceDetailList.map((conferenceDetailList) => {
          conferenceDetailList.view = viewCheck(conferenceDetailList.number);
        });
      }

      pageLoadComplete.value = true;
    })
    .catch((error) => {
      console.error(error);
    });
};

// getConferenceList();

const viewCheck = (number) => {
  if (number === 5) {
    //23년 5월 12일 오후 5시 이후부터 보여지도록
    const date = new Date();
    const targetDate = new Date("2023-05-12T17:00:00");
    return date > targetDate;
  } else {
    return true;
  }
};

const thisPageName = currentRoute._value.name;
console.log(thisPageName);

const tokenTest = ref("");

const removeStorageItem = () => {
  if (localStorage.getItem("sns_type")) {
    localStorage.removeItem("sns_type");
  }
  if (localStorage.getItem("sns_id")) {
    localStorage.removeItem("sns_id");
  }
  if (localStorage.getItem("uid")) {
    localStorage.removeItem("uid");
  }
  if (localStorage.getItem("email")) {
    localStorage.removeItem("email");
  }
  if (localStorage.getItem("name")) {
    localStorage.removeItem("name");
  }
  if (localStorage.getItem("phone")) {
    localStorage.removeItem("phone");
  }
};

const pageLoadComplete = ref(false);
const conferenceIdx = ref(0);
onMounted(() => {
  conferenceIdx.value = currentRoute._value.params.idx;
  console.log(conferenceIdx.value);
  tokenTest.value = localStorage.getItem("authorization");
  getConferenceList();

  // sns 로그인시 사용되는 스토리지 삭제
  removeStorageItem();
});
</script>

<template>
  <div
    v-if="pageLoadComplete === false"
    class="flex justify-center items-center w-full min-h-[calc(100vh-var(--nav-height,80px))]"
  >
    <span class="loader"></span>
  </div>

  <div
    v-else
    class="w-full max-w-[75rem] mx-auto px-4 sm:px-6 lg:px-8 pt-6 lg:pt-10 pb-16 lg:pb-24"
  >
    <!-- 목록으로 -->
    <router-link :to="{ name: 'eduList' }" class="back-link">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke-width="2"
        stroke="currentColor"
        class="w-4 h-4"
        aria-hidden="true"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          d="M15.75 19.5 8.25 12l7.5-7.5"
        />
      </svg>
      컨퍼런스 목록
    </router-link>

    <!-- 제목 & 요약 -->
    <header class="mt-4 lg:mt-6 pb-6 lg:pb-8 border-b border-gray-200">
      <div class="flex flex-wrap items-center gap-2">
        <span class="status-chip" :class="`status-chip--${overallPeriod}`">
          {{ PERIOD_LABELS[overallPeriod] }}
        </span>
        <span
          v-if="state.conference.category"
          class="text-sm font-medium text-gray-500"
        >
          {{ state.conference.category }}
        </span>
      </div>

      <h1 class="conference-title">{{ state.conference.title }}</h1>

      <dl class="mt-5 flex flex-col sm:flex-row sm:flex-wrap gap-x-8 gap-y-2.5">
        <div v-if="scheduleRange" class="summary-item">
          <dt class="summary-label">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.8"
              stroke="currentColor"
              class="w-[1.125rem] h-[1.125rem]"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
              />
            </svg>
            일정
          </dt>
          <dd>{{ scheduleRange }}</dd>
        </div>
        <div v-if="locals.length > 0" class="summary-item">
          <dt class="summary-label">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.8"
              stroke="currentColor"
              class="w-[1.125rem] h-[1.125rem]"
              aria-hidden="true"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15 10.5a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1 1 15 0Z"
              />
            </svg>
            지역
          </dt>
          <dd>{{ locals.join(" · ") }}</dd>
        </div>
      </dl>

      <!-- 모바일: 접수 영역 바로가기 -->
      <button type="button" class="jump-button" @click="scrollToRegistration">
        컨퍼런스 접수하기
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="2"
          stroke="currentColor"
          class="w-4 h-4"
          aria-hidden="true"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M19.5 13.5 12 21m0 0-7.5-7.5M12 21V3"
          />
        </svg>
      </button>
    </header>

    <div
      class="mt-8 lg:mt-10 grid items-start gap-12 lg:grid-cols-[minmax(0,1fr)_23rem] xl:grid-cols-[minmax(0,1fr)_25rem] xl:gap-14"
    >
      <!-- 상세 내용 -->
      <article
        class="conference-body"
        v-html="state.conference.contents"
      ></article>

      <!-- 접수 -->
      <aside
        ref="registrationRef"
        class="scroll-mt-24"
        aria-labelledby="registration-title"
      >
        <h2 id="registration-title" class="registration-title">
          컨퍼런스 접수
        </h2>
        <p class="mt-1 text-sm text-gray-500 break-keep">
          일정을 확인하고 참석할 부서를 신청하세요.
        </p>

        <ul class="mt-5 space-y-3">
          <li
            v-for="session in sessions"
            :key="session.number"
            :id="'conference_' + session.number"
            class="session"
            :class="{ 'session--open': isExpanded(session) }"
          >
            <button
              type="button"
              class="session-header"
              :aria-expanded="isExpanded(session)"
              :aria-controls="'session-depts-' + session.number"
              @click="toggleSession(session)"
            >
              <span class="session-badge mont">{{ session.label }}</span>

              <span class="min-w-0 grow basis-0">
                <span class="flex items-start justify-between gap-2">
                  <span class="session-place">{{ session.place }}</span>
                  <span
                    class="status-chip mt-0.5"
                    :class="`status-chip--${session.period}`"
                  >
                    {{ PERIOD_LABELS[session.period] }}
                  </span>
                </span>
                <span class="session-meta">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.8"
                    stroke="currentColor"
                    class="session-meta-icon"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5"
                    />
                  </svg>
                  {{ formatSchedule(session.schedule) }}
                </span>
                <span class="session-meta">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.8"
                    stroke="currentColor"
                    class="session-meta-icon"
                    aria-hidden="true"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                    />
                  </svg>
                  <span>
                    등록 마감
                    <b class="font-semibold text-gray-800">
                      {{ formatDeadline(session.redatetime) }}
                    </b>
                  </span>
                </span>
              </span>

              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="2"
                stroke="currentColor"
                class="mt-1 w-5 h-5 shrink-0 text-gray-400 transition-transform duration-200"
                :class="{ 'rotate-180': isExpanded(session) }"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="m19.5 8.25-7.5 7.5-7.5-7.5"
                />
              </svg>
            </button>

            <ul
              v-show="isExpanded(session)"
              :id="'session-depts-' + session.number"
              class="dept-list"
            >
              <li
                v-for="dept in session.depts"
                :key="dept.idx"
                class="dept-row"
              >
                <div class="min-w-0">
                  <p class="font-medium text-gray-900 break-keep">
                    {{ dept.dept }}
                  </p>
                  <p v-if="showSeats" class="mt-0.5 text-xs text-gray-500">
                    잔여 <b class="font-semibold">{{ dept.left_personnel }}</b
                    >석
                  </p>
                </div>
                <button
                  type="button"
                  class="dept-action"
                  :class="{
                    'dept-action--disabled': getDeptAction(dept).disabled,
                  }"
                  :aria-disabled="getDeptAction(dept).disabled"
                  @click="getDeptAction(dept).onClick()"
                >
                  {{ getDeptAction(dept).label }}
                </button>
              </li>
            </ul>
          </li>
        </ul>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.back-link {
  @apply inline-flex items-center gap-1 text-sm font-medium text-gray-500 transition-colors hover:text-gray-900;
}

.conference-title {
  @apply mt-3 text-[1.625rem] md:text-[2.25rem] font-bold leading-snug tracking-tight text-gray-900 break-keep;
}

.summary-item {
  @apply flex items-center gap-3 text-[0.9375rem] md:text-base text-gray-800;
}

.summary-label {
  @apply inline-flex shrink-0 items-center gap-1.5 text-sm font-medium text-gray-500;
}

.jump-button {
  @apply mt-6 flex lg:hidden w-full h-12 items-center justify-center gap-2 rounded-xl bg-main-navy text-[0.9375rem] font-semibold text-white transition-colors hover:bg-main-navy/90;
}

/* 접수 상태 */
.status-chip {
  @apply inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold leading-none;
}

.status-chip::before {
  content: "";
  @apply h-1.5 w-1.5 rounded-full bg-current;
}

.status-chip--open {
  @apply bg-emerald-50 text-emerald-700;
}

.status-chip--before {
  @apply bg-amber-50 text-amber-700;
}

.status-chip--closed {
  @apply bg-gray-100 text-gray-500;
}

/* 상세 내용 (에디터 HTML) */
.conference-body {
  @apply min-w-0 overflow-hidden rounded-2xl text-base leading-relaxed text-gray-700 break-keep;
}

.conference-body :deep(p) {
  margin: 0;
}

.conference-body :deep(img) {
  display: block;
  width: 100%;
  height: auto;
}

.conference-body :deep(a:not(:has(img))) {
  @apply text-main-navy underline underline-offset-2;
}

/* 접수 */
.registration-title {
  @apply text-xl md:text-[1.375rem] font-bold tracking-tight text-gray-900;
}

.session {
  @apply overflow-hidden rounded-2xl border border-gray-200 bg-white transition-shadow;
}

.session--open {
  @apply shadow-[0_8px_24px_-12px_rgba(15,23,42,0.18)];
}

.session-header {
  @apply flex w-full items-start gap-3 p-4 md:p-5 text-left transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-main-navy;
}

.session-badge {
  @apply inline-flex h-8 min-w-[2.75rem] shrink-0 items-center justify-center rounded-lg bg-main-navy px-2 text-sm font-bold text-white;
}

.session-place {
  @apply block text-base md:text-[1.0625rem] font-bold leading-snug text-gray-900 break-keep;
}

.session-meta {
  @apply mt-1.5 flex items-start gap-1.5 text-sm leading-snug text-gray-600 break-keep;
}

.session-meta-icon {
  @apply mt-px h-4 w-4 shrink-0 text-gray-400;
}

.dept-list {
  @apply divide-y divide-gray-200/70 border-t border-gray-100 bg-gray-50 px-4 md:px-5;
}

.dept-row {
  @apply flex items-center justify-between gap-3 py-3;
}

.dept-action {
  @apply inline-flex h-9 min-w-[5.5rem] shrink-0 items-center justify-center rounded-lg bg-main-navy px-4 text-sm font-semibold text-white transition-colors hover:bg-main-navy/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-main-navy focus-visible:ring-offset-2;
}

.dept-action--disabled {
  @apply cursor-not-allowed bg-gray-200 text-gray-900 hover:bg-gray-200;
}
</style>
